import React, { useState, useCallback, useRef } from "react";
import { Plus, Trash2, GripVertical, Sparkles, Eye, Save, Loader2, X, Search, ArrowLeft, LayoutGrid, Columns, Square, Grid3X3 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── PRESET LAYOUT TEMPLATES ────────────────────────
const LAYOUT_PRESETS = [
  {
    id: "classic",
    name: "Classic",
    desc: "1 large + 3 small",
    icon: LayoutGrid,
    columns: 3,
    boxes: [
      { id: "b1", col: 0, row: 0, colSpan: 2, rowSpan: 3, label: "Main Outfit" },
      { id: "b2", col: 2, row: 0, colSpan: 1, rowSpan: 1, label: "Accessory 1" },
      { id: "b3", col: 2, row: 1, colSpan: 1, rowSpan: 1, label: "Accessory 2" },
      { id: "b4", col: 2, row: 2, colSpan: 1, rowSpan: 1, label: "Accessory 3" },
    ],
  },
  {
    id: "duo",
    name: "Side by Side",
    desc: "2 equal outfits",
    icon: Columns,
    columns: 2,
    boxes: [
      { id: "b1", col: 0, row: 0, colSpan: 1, rowSpan: 2, label: "Outfit 1" },
      { id: "b2", col: 1, row: 0, colSpan: 1, rowSpan: 2, label: "Outfit 2" },
    ],
  },
  {
    id: "family",
    name: "Family Look",
    desc: "3 outfits + 2 accessories",
    icon: Grid3X3,
    columns: 3,
    boxes: [
      { id: "b1", col: 0, row: 0, colSpan: 1, rowSpan: 2, label: "For Her" },
      { id: "b2", col: 1, row: 0, colSpan: 1, rowSpan: 2, label: "For Him" },
      { id: "b3", col: 2, row: 0, colSpan: 1, rowSpan: 2, label: "For Kids" },
      { id: "b4", col: 0, row: 2, colSpan: 1, rowSpan: 1, label: "Accessory 1" },
      { id: "b5", col: 1, row: 2, colSpan: 1, rowSpan: 1, label: "Accessory 2" },
    ],
  },
  {
    id: "duo-plus",
    name: "Duo + Accessories",
    desc: "2 outfits + 2 side",
    icon: Square,
    columns: 4,
    boxes: [
      { id: "b1", col: 0, row: 0, colSpan: 2, rowSpan: 2, label: "Outfit 1" },
      { id: "b2", col: 2, row: 0, colSpan: 2, rowSpan: 2, label: "Outfit 2" },
      { id: "b3", col: 0, row: 2, colSpan: 1, rowSpan: 1, label: "Accessory 1" },
      { id: "b4", col: 1, row: 2, colSpan: 1, rowSpan: 1, label: "Accessory 2" },
    ],
  },
  {
    id: "hero-grid",
    name: "Hero + Grid",
    desc: "1 large + 4 small",
    icon: LayoutGrid,
    columns: 4,
    boxes: [
      { id: "b1", col: 0, row: 0, colSpan: 2, rowSpan: 2, label: "Hero Outfit" },
      { id: "b2", col: 2, row: 0, colSpan: 1, rowSpan: 1, label: "Piece 1" },
      { id: "b3", col: 3, row: 0, colSpan: 1, rowSpan: 1, label: "Piece 2" },
      { id: "b4", col: 2, row: 1, colSpan: 1, rowSpan: 1, label: "Piece 3" },
      { id: "b5", col: 3, row: 1, colSpan: 1, rowSpan: 1, label: "Piece 4" },
    ],
  },
];

// ─── PRODUCT PICKER MODAL ────────────────────────
function ProductPicker({ products, onSelect, onClose, currentProductId }) {
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();
  const filtered = products.filter(p => {
    const matchSearch = !search || (p.name || "").toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === "all" || p.category === catFilter;
    return matchSearch && matchCat;
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="product-picker-modal">
        <div className="p-4 border-b border-[#282828]/10 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#282828]">Select Product <span className="text-[#676664] font-normal">(any category)</span></h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 border-b border-[#282828]/10 flex gap-2">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#676664]/50" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search all products..." className="w-full pl-8 pr-3 py-2 text-sm border border-[#282828]/15 outline-none focus:border-[#9A7B4F]" data-testid="picker-search" />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="text-xs border border-[#282828]/15 px-2 py-2 bg-white outline-none" data-testid="picker-category-filter">
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          <div className="grid grid-cols-5 gap-2">
            {filtered.slice(0, 60).map(p => (
              <button key={p.id} onClick={() => { onSelect(p); onClose(); }}
                className={`text-left transition-all hover:ring-2 hover:ring-[#9A7B4F]/50 ${currentProductId === p.id ? 'ring-2 ring-[#9A7B4F]' : ''}`}
                data-testid={`pick-product-${p.id}`}>
                <div className="aspect-[3/4] bg-[#f5f3f0] overflow-hidden">
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" loading="lazy" />}
                </div>
                <p className="text-[9px] text-[#282828] truncate px-1 pt-0.5">{p.name}</p>
                <p className="text-[8px] text-[#9A7B4F] px-1 pb-0.5">{p.category}</p>
              </button>
            ))}
          </div>
          {filtered.length === 0 && <p className="text-center text-sm text-[#676664] py-8">No products found</p>}
        </div>
      </div>
    </div>
  );
}


// ─── LAYOUT PREVIEW (customer-facing render) ────────────
function LayoutPreview({ layout, productMap, lookName }) {
  if (!layout || !layout.boxes) return null;
  const { columns = 3, boxes = [] } = layout;
  const maxRow = Math.max(...boxes.map(b => b.row + b.rowSpan), 1);
  const cellH = 80;

  return (
    <div className="bg-white border border-[#282828]/10 p-4" data-testid="layout-preview">
      <p className="text-[10px] text-[#676664] tracking-wider uppercase mb-3">Customer Preview</p>
      <div className="bg-[#f5f3f0] p-2 aspect-[4/5] max-w-[320px] mx-auto overflow-hidden">
        <div className="relative w-full h-full" style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gridTemplateRows: `repeat(${maxRow}, 1fr)`, gap: "2px" }}>
          {boxes.map(box => {
            const prod = productMap[box.product_id];
            return (
              <div key={box.id} style={{ gridColumn: `${box.col + 1} / span ${box.colSpan}`, gridRow: `${box.row + 1} / span ${box.rowSpan}` }} className="overflow-hidden bg-[#E8E0D5] relative">
                {prod?.images?.[0] ? (
                  <img src={prod.images[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[8px] text-[#676664]/40">{box.label || "Empty"}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {lookName && <p className="text-center mt-2 text-[13px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>{lookName}</p>}
    </div>
  );
}


// ─── MAIN LAYOUT BUILDER COMPONENT ────────────────────────
export default function LayoutBuilder({ look, products, headers, onSaved, onClose }) {
  const [layout, setLayout] = useState(() => {
    if (look.layout && look.layout.boxes) return look.layout;
    // Default: derive from current items using classic layout
    const preset = LAYOUT_PRESETS[0];
    const boxes = preset.boxes.map((b, i) => ({
      ...b,
      product_id: look.product_ids?.[i] || look.items?.[i]?.id || "",
    }));
    return { columns: preset.columns, boxes };
  });
  const [targetCategory, setTargetCategory] = useState(look.target_category || "");
  const [saving, setSaving] = useState(false);
  const [pickerBox, setPickerBox] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [dragBoxId, setDragBoxId] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const counterRef = useRef(layout.boxes.length);

  const productMap = {};
  for (const p of products) productMap[p.id] = p;

  const maxRow = Math.max(...layout.boxes.map(b => b.row + b.rowSpan), 1);

  // Apply a preset template
  const applyPreset = (preset) => {
    const boxes = preset.boxes.map((b, i) => ({
      ...b,
      product_id: layout.boxes[i]?.product_id || "",
    }));
    setLayout({ columns: preset.columns, boxes });
  };

  // Update a specific box
  const updateBox = (boxId, updates) => {
    setLayout(prev => ({
      ...prev,
      boxes: prev.boxes.map(b => b.id === boxId ? { ...b, ...updates } : b),
    }));
  };

  // Add a new box
  const addBox = () => {
    counterRef.current += 1;
    const newId = `b${counterRef.current}`;
    const row = maxRow;
    setLayout(prev => ({
      ...prev,
      boxes: [...prev.boxes, { id: newId, col: 0, row, colSpan: 1, rowSpan: 1, label: `Slot ${counterRef.current}`, product_id: "" }],
    }));
  };

  // Remove a box
  const removeBox = (boxId) => {
    setLayout(prev => ({
      ...prev,
      boxes: prev.boxes.filter(b => b.id !== boxId),
    }));
  };

  // Drag-and-drop reorder
  const handleDragStart = (boxId) => setDragBoxId(boxId);
  const handleDragOver = (e, targetBoxId) => {
    e.preventDefault();
    if (!dragBoxId || dragBoxId === targetBoxId) return;
    setLayout(prev => {
      const boxes = [...prev.boxes];
      const fromIdx = boxes.findIndex(b => b.id === dragBoxId);
      const toIdx = boxes.findIndex(b => b.id === targetBoxId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      // Swap positions
      const fromBox = boxes[fromIdx];
      const toBox = boxes[toIdx];
      const tempCol = fromBox.col, tempRow = fromBox.row;
      boxes[fromIdx] = { ...fromBox, col: toBox.col, row: toBox.row };
      boxes[toIdx] = { ...toBox, col: tempCol, row: tempRow };
      return { ...prev, boxes };
    });
  };
  const handleDragEnd = () => setDragBoxId(null);

  // AI generate layout suggestion
  const handleAiGenerate = async () => {
    setAiGenerating(true);
    try {
      const r = await fetch(`${API}/ai/admin/generate-looks-batch`, { method: "POST", headers });
      if (!r.ok) throw new Error();
      const d = await r.json();
      if (d.looks?.length > 0) {
        const newLook = d.looks[0];
        const newProductIds = newLook.product_ids || [];
        // Apply these products to the current layout
        setLayout(prev => ({
          ...prev,
          boxes: prev.boxes.map((b, i) => ({ ...b, product_id: newProductIds[i] || b.product_id })),
        }));
        toast.success("AI suggested products applied to layout");
      } else {
        toast.info("AI couldn't generate suggestions");
      }
    } catch { toast.error("AI generation failed"); }
    setAiGenerating(false);
  };

  // Save
  const handleSave = async () => {
    const filledBoxes = layout.boxes.filter(b => b.product_id);
    if (filledBoxes.length < 2) { toast.error("Assign at least 2 products"); return; }
    setSaving(true);
    try {
      const productIds = layout.boxes.filter(b => b.product_id).map(b => b.product_id);
      const r = await fetch(`${API}/ai/admin/looks/${look.id}`, {
        method: "PUT", headers,
        body: JSON.stringify({ layout, product_ids: productIds, target_category: targetCategory }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      onSaved(d.look || { ...look, layout, product_ids: productIds, target_category: targetCategory });
      toast.success("Layout saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  // Get categories for target_category picker
  const availableCategories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="layout-builder-modal">
      <div className="bg-white w-full max-w-[1100px] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#282828]/10 bg-[#FAF8F0]">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1 hover:bg-[#282828]/5 rounded"><ArrowLeft className="w-4 h-4" /></button>
            <div>
              <h2 className="text-base text-[#282828] font-medium" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Layout Builder — {look.name || "Untitled"}</h2>
              <p className="text-[10px] text-[#676664]">Drag boxes to reposition, click to assign products from any category</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)} className="text-xs gap-1.5" data-testid="toggle-preview">
              <Eye className="w-3 h-3" /> {showPreview ? "Hide" : "Show"} Preview
            </Button>
            <Button onClick={handleAiGenerate} disabled={aiGenerating} variant="outline" className="text-xs gap-1.5" data-testid="ai-suggest-btn">
              {aiGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Fill
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white text-xs gap-1.5 px-5" data-testid="save-layout-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save Layout
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto flex">
          {/* Left: Editor */}
          <div className="flex-1 p-5 space-y-5 overflow-y-auto">
            {/* Target Category */}
            <div className="flex items-center gap-4">
              <label className="text-[10px] text-[#676664] tracking-wider uppercase whitespace-nowrap">Target Category</label>
              <select value={targetCategory} onChange={e => setTargetCategory(e.target.value)}
                className="text-xs border border-[#282828]/15 px-3 py-1.5 bg-white outline-none flex-1 max-w-xs" data-testid="target-category-select">
                <option value="">All / General</option>
                {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Preset Templates */}
            <div>
              <p className="text-[10px] text-[#676664] tracking-wider uppercase mb-2">Quick Templates</p>
              <div className="flex gap-2 flex-wrap">
                {LAYOUT_PRESETS.map(p => (
                  <button key={p.id} onClick={() => applyPreset(p)}
                    className="flex items-center gap-2 px-3 py-2 border border-[#282828]/10 hover:border-[#9A7B4F]/40 hover:bg-[#9A7B4F]/5 transition-colors text-left"
                    data-testid={`preset-${p.id}`}>
                    <p.icon className="w-4 h-4 text-[#9A7B4F]" />
                    <div>
                      <p className="text-[11px] text-[#282828] font-medium">{p.name}</p>
                      <p className="text-[9px] text-[#676664]">{p.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Grid Settings */}
            <div className="flex items-center gap-4">
              <label className="text-[10px] text-[#676664] tracking-wider uppercase">Columns</label>
              <div className="flex gap-1">
                {[2, 3, 4].map(n => (
                  <button key={n} onClick={() => setLayout(prev => ({ ...prev, columns: n }))}
                    className={`w-8 h-8 text-xs font-medium border transition-colors ${layout.columns === n ? 'bg-[#282828] text-white border-[#282828]' : 'border-[#282828]/15 text-[#676664] hover:border-[#282828]/40'}`}
                    data-testid={`col-${n}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid Editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] text-[#676664] tracking-wider uppercase">Layout Boxes ({layout.boxes.length})</p>
                <button onClick={addBox} className="flex items-center gap-1 text-[10px] text-[#9A7B4F] hover:underline" data-testid="add-box-btn">
                  <Plus className="w-3 h-3" /> Add Box
                </button>
              </div>

              {/* Visual Grid */}
              <div className="border border-[#282828]/10 bg-[#FAF8F0] p-3" style={{ display: "grid", gridTemplateColumns: `repeat(${layout.columns}, 1fr)`, gridTemplateRows: `repeat(${maxRow}, 80px)`, gap: "4px" }} data-testid="layout-grid">
                {layout.boxes.map(box => {
                  const prod = productMap[box.product_id];
                  return (
                    <div
                      key={box.id}
                      draggable
                      onDragStart={() => handleDragStart(box.id)}
                      onDragOver={(e) => handleDragOver(e, box.id)}
                      onDragEnd={handleDragEnd}
                      style={{ gridColumn: `${box.col + 1} / span ${box.colSpan}`, gridRow: `${box.row + 1} / span ${box.rowSpan}` }}
                      className={`border-2 border-dashed relative group transition-colors cursor-grab active:cursor-grabbing ${dragBoxId === box.id ? 'opacity-40 border-[#9A7B4F]' : prod ? 'border-[#9A7B4F]/30 bg-white' : 'border-[#282828]/15 bg-white/50 hover:border-[#9A7B4F]/40'}`}
                      data-testid={`grid-box-${box.id}`}
                    >
                      {/* Drag handle */}
                      <div className="absolute top-1 left-1 z-10 p-0.5 bg-white/80 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="w-3 h-3 text-[#676664]" />
                      </div>

                      {/* Remove */}
                      <button onClick={(e) => { e.stopPropagation(); removeBox(box.id); }} className="absolute top-1 right-1 z-10 p-0.5 bg-white/80 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50" data-testid={`remove-box-${box.id}`}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </button>

                      {/* Content */}
                      <button onClick={() => setPickerBox(box.id)} className="w-full h-full flex flex-col items-center justify-center p-1">
                        {prod ? (
                          <div className="w-full h-full overflow-hidden relative">
                            {prod.images?.[0] && <img src={prod.images[0]} alt="" className="w-full h-full object-cover" />}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                              <p className="text-[8px] text-white truncate">{prod.name}</p>
                              <p className="text-[7px] text-white/70">{prod.category}</p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 text-[#676664]/30 mb-1" />
                            <p className="text-[9px] text-[#676664]/50">{box.label || "Click to add"}</p>
                          </>
                        )}
                      </button>

                      {/* Size controls */}
                      <div className="absolute bottom-1 left-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); updateBox(box.id, { colSpan: Math.max(1, box.colSpan - 1) }); }}
                          className="w-4 h-4 bg-white border border-[#282828]/20 text-[8px] text-[#676664] hover:bg-[#282828]/5 flex items-center justify-center" title="Narrower">-W</button>
                        <button onClick={(e) => { e.stopPropagation(); updateBox(box.id, { colSpan: Math.min(layout.columns, box.colSpan + 1) }); }}
                          className="w-4 h-4 bg-white border border-[#282828]/20 text-[8px] text-[#676664] hover:bg-[#282828]/5 flex items-center justify-center" title="Wider">+W</button>
                        <button onClick={(e) => { e.stopPropagation(); updateBox(box.id, { rowSpan: Math.max(1, box.rowSpan - 1) }); }}
                          className="w-4 h-4 bg-white border border-[#282828]/20 text-[8px] text-[#676664] hover:bg-[#282828]/5 flex items-center justify-center" title="Shorter">-H</button>
                        <button onClick={(e) => { e.stopPropagation(); updateBox(box.id, { rowSpan: box.rowSpan + 1 }); }}
                          className="w-4 h-4 bg-white border border-[#282828]/20 text-[8px] text-[#676664] hover:bg-[#282828]/5 flex items-center justify-center" title="Taller">+H</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Box list (table view for fine-tuning labels) */}
            <div>
              <p className="text-[10px] text-[#676664] tracking-wider uppercase mb-2">Box Details</p>
              <div className="space-y-1">
                {layout.boxes.map(box => {
                  const prod = productMap[box.product_id];
                  return (
                    <div key={box.id} className="flex items-center gap-3 p-2 bg-[#FAF8F0] border border-[#282828]/5 text-[11px]">
                      <div className="w-8 h-8 bg-[#E8E0D5] overflow-hidden flex-shrink-0">
                        {prod?.images?.[0] && <img src={prod.images[0]} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <input value={box.label || ""} onChange={e => updateBox(box.id, { label: e.target.value })}
                        className="flex-1 bg-transparent border-b border-transparent hover:border-[#282828]/15 focus:border-[#9A7B4F] outline-none py-0.5 text-[#282828]"
                        placeholder="Label..." data-testid={`box-label-${box.id}`} />
                      <span className="text-[#676664] text-[10px] whitespace-nowrap">{prod ? prod.name : "No product"}</span>
                      <span className="text-[#9A7B4F] text-[9px]">{prod?.category || ""}</span>
                      <span className="text-[#676664]/40 text-[9px]">{box.colSpan}x{box.rowSpan}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Preview */}
          {showPreview && (
            <div className="w-[340px] border-l border-[#282828]/10 p-5 bg-[#FAF8F0] overflow-y-auto">
              <LayoutPreview layout={layout} productMap={productMap} lookName={look.name} />
            </div>
          )}
        </div>
      </div>

      {/* Product Picker */}
      {pickerBox && (
        <ProductPicker
          products={products}
          currentProductId={layout.boxes.find(b => b.id === pickerBox)?.product_id}
          onSelect={(product) => updateBox(pickerBox, { product_id: product.id })}
          onClose={() => setPickerBox(null)}
        />
      )}
    </div>
  );
}
